'use client';

import * as React from 'react';

function cn(...cls: (string | false | null | undefined)[]) {
  return cls.filter(Boolean).join(' ');
}

const nf = new Intl.NumberFormat('ru-RU');
const cf = new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB', maximumFractionDigits: 0 });

type Mode = 'online' | 'blended' | 'offline';

type CalcState = {
  people: number;
  modules: number;
  hours: number;
  rate: number;
  lms: number;
  coachH: number;
  mode: Mode;
  discSel: 'auto' | '0' | '5' | '10';
};

const defaultState: CalcState = {
  people: 25,
  modules: 4,
  hours: 3,
  rate: 6000,
  lms: 1200,
  coachH: 0,
  mode: 'blended',
  discSel: 'auto',
};

type FormData = {
  name: string;
  phone: string;
  task: string;
  email: string;
  agreed: boolean;
};

function modeCoef(m: Mode) {
  switch (m) {
    case 'offline':
      return 1.15;
    case 'blended':
      return 1.05;
    default:
      return 1.0;
  }
}

function autoDisc(people: number) {
  return people >= 50 ? 0.1 : people >= 20 ? 0.05 : 0;
}

function useLearningCalc(defaults: CalcState) {
  const [state, setState] = React.useState<CalcState>(() => {
    if (typeof window !== 'undefined') {
      const cached = window.localStorage.getItem('learning_calc');
      if (cached) {
        try {
          return { ...defaults, ...JSON.parse(cached) } as CalcState;
        } catch { }
      }
    }
    return defaults;
  });

  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('learning_calc', JSON.stringify(state));
    }
  }, [state]);

  return [state, setState] as const;
}

function InputNumber({
  label,
  value,
  onChange,
  min,
  step = 1,
}: {
  label: string;
  value: number;
  onChange: (n: number) => void;
  min?: number;
  step?: number;
}) {
  return (
    <label className="block">
      <span className="text-sm text-slate-600">{label}</span>
      <input
        type="number"
        value={Number.isFinite(value) ? value : 0}
        onChange={(e) => onChange(Number(e.target.value || 0))}
        min={min}
        step={step}
        className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-blue-600 focus:outline-none"
      />
    </label>
  );
}

function Select<T extends string>({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: string }[];
}) {
  return (
    <label className="block">
      <span className="text-sm text-slate-600">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as T)}
        className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-blue-600 focus:outline-none"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function Card({ className, children }: React.PropsWithChildren<{ className?: string }>) {
  return (
    <div className={cn('rounded-2xl bg-white p-6 shadow-[0_6px_18px_rgba(0,0,0,0.06)]', className)}>
      {children}
    </div>
  );
}

function KPI({ icon, value, label }: { icon: React.ReactNode; value: string; label: string }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-white/30 bg-white/10 px-4 py-2 text-white backdrop-blur h-[72px]">
      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white/60 text-base font-bold">
        {icon}
      </div>
      <div>
        <div className="text-xl font-extrabold leading-none">{value}</div>
        <div className="text-sm opacity-90 leading-none">{label}</div>
      </div>
    </div>
  );
}

function Section({
  id,
  children,
  gray = false,
  className,
}: React.PropsWithChildren<{ id?: string; gray?: boolean; className?: string }>) {
  return (
    <section id={id} className={cn('py-20', gray && 'bg-slate-50', className)}>
      <div className="mx-auto max-w-6xl px-6">{children}</div>
    </section>
  );
}

function getStatusColorClass(status: string) {
  if (status.includes('Ошибка') || status.includes('сети')) return 'text-red-600 font-medium';
  if (status.includes('Пожалуйста')) return 'text-amber-600 font-medium';
  if (status.includes('успешно')) return 'text-green-600 font-medium';
  return 'text-slate-500';
}

export default function LearningPage() {
  const [state, setState] = useLearningCalc(defaultState);

  const calc = React.useMemo(() => {
    const hh = state.people * state.modules * state.hours;
    const trainerCost = hh * state.rate * modeCoef(state.mode);
    const lmsCost = state.people * state.lms;
    const coachCost = state.people * state.coachH * state.rate;
    const subtotal = trainerCost + lmsCost + coachCost;
    const discPct = state.discSel === 'auto' ? autoDisc(state.people) : Number(state.discSel) / 100;
    const discount = subtotal * discPct;
    const total = subtotal - discount;
    return { hh, trainerCost, lmsCost, coachCost, discPct, discount, total };
  }, [state]);

  const [formData, setFormData] = React.useState<FormData>({
    name: '',
    phone: '',
    task: '',
    email: '',
    agreed: false,
  });

  const [formStatus, setFormStatus] = React.useState('');

  const handleSubmit = async (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    e.preventDefault();

    if (!formData.agreed) {
      setFormStatus('Пожалуйста, дайте согласие на обработку персональных данных.');
      return;
    }

    if (!formData.name.trim() || !formData.phone.trim() || !formData.email.trim()) {
      setFormStatus('Пожалуйста, заполните Имя, Телефон и Email для отправки заявки.');
      return;
    }

    setFormStatus('Отправка...');

    const apiUrl = `${process.env.NEXT_PUBLIC_STRAPI_API_URL}/api/learning-programs`;

    const { agreed, ...dataToSend } = formData;

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          data: {
            ...dataToSend,
            formName: 'Обучение и развитие персонала' 
          },
        }),
      });

      if (response.ok) {
        setFormStatus('Ваша заявка успешно отправлена! Скоро мы с вами свяжемся.');
        setFormData({ name: '', phone: '', task: '', email: '', agreed: false });
      } else {
        let errorMsg = 'Произошла ошибка при обработке данных.';
        try {
          const errorData = await response.json();
          const details = errorData.error?.details?.errors?.[0];
          if (details) {
            errorMsg = `Ошибка поля "${details.path[0]}": ${details.message}`;
          } else if (errorData.error?.message) {
            errorMsg = errorData.error.message;
          }
          console.error('Ошибка API Strapi:', errorData);
        } catch {
          errorMsg = `Ошибка сети или сервера (${response.status} ${response.statusText}).`;
        }
        setFormStatus(`Произошла ошибка: ${errorMsg}`);
      }
    } catch (error) {
      setFormStatus('Ошибка сети. Пожалуйста, проверьте ваше подключение и запущен ли Strapi.');
      console.error('Ошибка fetch/сети:', error);
    }
  };


  return (
    <main className="bg-white text-slate-900">
      <section className="mt-20 bg-gradient-to-br from-[#00214F] to-[#003B9B] text-white pt-24 pb-20">
        <div className="mx-auto max-w-6xl pl-1 pr-6">
          <h1 className="text-4xl font-black md:text-5xl">Обучение и развитие персонала</h1>
          <p className="mt-3 max-w-3xl text-base md:text-lg opacity-95">
            Собираем программы под задачи бизнеса: обучение линейных и специалистов, развитие руководителей, стандарты сервиса, основы бережливого производства. Доступны смешанные форматы, система управления обучением (LMS) и прозрачные метрики.
          </p>
          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <a
              href="#calc"
              className="w-full sm:w-auto inline-flex items-center justify-center bg-[#FF7C22] text-white py-3 px-5 rounded-xl font-bold transition-all duration-200 ease-in-out hover:brightness-[105%] hover:-translate-y-[1px] text-base shadow-lg"
            >
              Рассчитать программу
            </a>
            <a
              href="#tracks"
              className="w-full sm:w-auto inline-flex items-center justify-center bg-white text-[#003B9B] border-2 border-[#003B9B] py-3 px-5 rounded-xl font-bold transition-all duration-200 ease-in-out hover:brightness-[105%] hover:-translate-y-[1px] text-base shadow-lg"
            >
              Треки и форматы
            </a>
          </div>

          <div className="mt-7 rounded-2xl border border-white/30 bg-white/10 p-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <KPI icon="🚀" value="7–14 дней" label="запуск программы" />
              <KPI icon="🎯" value="80%+" label="завершение у данной групп" />
              <KPI icon="📊" value="NPS 60+" label="оценка умножения" />
              <KPI icon="📅" value="Еженед." label="резервы перечня" />
            </div>
          </div>
        </div>
      </section>

      <Section id="tracks" gray>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          <Card>
            <h3 className="text-xl font-extrabold">Направления и компетенции</h3>
            <ul className="mt-3 list-disc space-y-1 pl-5 text-base text-black">
              <li>Гибкие навыки: коммуникации, командная работа, управление временем</li>
              <li>Продажи и обслуживание: B2B, активации, работа с возражениями</li>
              <li>Лидерство: руководитель 0–1, коучинговый стиль, обратная связь</li>
              <li>Операционная эффективность: 5S, кайдзен, стандартные операционные карты</li>
              <li>HR для руководителей: адаптация новых сотрудников, индивидуальные консультации, цели и OKR</li>
            </ul>
          </Card>
          <Card>
            <h3 className="text-xl font-extrabold">Форматы</h3>
            <ul className="mt-3 list-disc space-y-1 pl-5 text-base text-black">
              <li>Семинар: офлайн/онлайн, 2–4 часа</li>
              <li>Микрообучение в системе управления обучением (блоки по 15–20 минут)</li>
              <li>Смешанный формат: теория + практика + наставничество</li>
              <li>На рабочем месте: контрольные списки, наблюдение за процессом, ретроспективы</li>
            </ul>
          </Card>
          <Card>
            <h3 className="text-xl font-extrabold">Метрики и контроль</h3>
            <ul className="mt-3 list-disc space-y-1 pl-5 text-base text-black">
              <li>Тестирование до и после обучения, практические задания</li>
              <li>Панели управления обучением: прогресс, завершение, оценки</li>
              <li>Перенос знаний: применение измерений</li>
              <li>Ретроспектива 30/60/90 и план внедрения</li>
            </ul>
          </Card>
        </div>
      </Section>

      <Section id="calc">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <Card>
            <h3 className="text-xl font-extrabold">Калькулятор программы</h3>
            <p className="mt-1 text-xs text-slate-500">
              Приблизительная оценка. Итоговая смета зависит от содержания, формата и региона.
            </p>

            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <InputNumber
                label="Участников, чел."
                value={state.people}
                onChange={(v) => setState((s) => ({ ...s, people: v }))}
                min={1}
              />
              <InputNumber
                label="Модулей"
                value={state.modules}
                onChange={(v) => setState((s) => ({ ...s, modules: v }))}
                min={1}
              />
              <InputNumber
                label="Часов на модуль"
                value={state.hours}
                onChange={(v) => setState((s) => ({ ...s, hours: v }))}
                min={1}
              />
              <InputNumber
                label="Ставка тренера, ₽/час"
                value={state.rate}
                onChange={(v) => setState((s) => ({ ...s, rate: v }))}
                min={0}
              />
              <InputNumber
                label="LMS/материалы, ₽ за участника"
                value={state.lms}
                onChange={(v) => setState((s) => ({ ...s, lms: v }))}
                min={0}
              />
              <InputNumber
                label="Коучинг, ч/участника (опц.)"
                value={state.coachH}
                onChange={(v) => setState((s) => ({ ...s, coachH: v }))}
                min={0}
                step={0.5}
              />
              <Select
                label="Формат"
                value={state.mode}
                onChange={(v) => setState((s) => ({ ...s, mode: v }))}
                options={[
                  { value: 'online', label: 'Онлайн' },
                  { value: 'blended', label: 'Смешанный' },
                  { value: 'offline', label: 'Оффлайн' },
                ]}
              />
              <Select
                label="Скидка за группу"
                value={state.discSel}
                onChange={(v) => setState((s) => ({ ...s, discSel: v }))}
                options={[
                  { value: 'auto', label: 'Авто (по числу участников)' },
                  { value: '0', label: 'Нет' },
                  { value: '5', label: '5%' },
                  { value: '10', label: '10%' },
                ]}
              />
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Card>
                <div className="text-xs text-slate-500">Учебное время</div>
                <div className="text-2xl font-extrabold">{nf.format(calc.hh)}</div>
                <div className="text-xs text-slate-500">человеко-часов</div>
              </Card>
              <Card>
                <div className="text-xs text-slate-500">Стоимость тренера</div>
                <div className="text-2xl font-extrabold">{cf.format(Math.round(calc.trainerCost))}</div>
              </Card>
              <Card>
                <div className="text-xs text-slate-500">LMS/материалы</div>
                <div className="text-2xl font-extrabold">{cf.format(Math.round(calc.lmsCost))}</div>
              </Card>
              <Card>
                <div className="text-xs text-slate-500">Коучинг</div>
                <div className="text-2xl font-extrabold">{cf.format(Math.round(calc.coachCost))}</div>
              </Card>
              <Card>
                <div className="text-xs text-slate-500">Скидка</div>
                <div className="text-2xl font-extrabold">
                  {(calc.discPct * 100).toFixed(0)}% / {cf.format(Math.round(calc.discount))}
                </div>
              </Card>
              <Card>
                <div className="text-xs text-slate-500">Итого</div>
                <div className="text-2xl font-extrabold text-[#003B9B]">{cf.format(Math.round(calc.total))}</div>
              </Card>
            </div>
            <p className="mt-2 text-xs text-slate-500">
              Коэффициенты: онлайн ×1.00, blended ×1.05, оффлайн ×1.15. Авто-скидка: 5% ≥ 20 чел., 10% ≥ 50 чел.
            </p>
          </Card>

          <Card>
            <h3 className="text-xl font-extrabold">Пакеты обучения</h3>
            <div className="mt-3 overflow-x-auto">
              <table className="w-full border-collapse text-base">
                <thead>
                  <tr className="text-left text-black">
                    <th className="border-b border-slate-200 px-2 py-2 font-medium">Пакет</th>
                    <th className="border-b border-slate-200 px-2 py-2 font-medium">Состав</th>
                    <th className="border-b border-slate-200 px-2 py-2 font-medium">Срок</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border-b border-slate-100 px-2 py-2">Starter</td>
                    <td className="border-b border-slate-100 px-2 py-2">2–3 модуля, онлайн, тесты + сертификаты</td>
                    <td className="border-b border-slate-100 px-2 py-2">2–4 недели</td>
                  </tr>
                  <tr>
                    <td className="border-b border-slate-100 px-2 py-2">Skills</td>
                    <td className="border-b border-slate-100 px-2 py-2">4–6 модулей, смешанный курс, задания и групповой коучинг</td>
                    <td className="border-b border-slate-100 px-2 py-2">4–8 недель</td>
                  </tr>
                  <tr>
                    <td className="px-2 py-2">Leadership</td>
                    <td className="px-2 py-2">6–8 модулей, офлайн/смешанный курс, индивидуальный коучинг</td>
                    <td className="px-2 py-2">6–10 недель</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="mt-2 text-xs text-slate-500">Методические материалы и отчётность входят. Адаптируем под отрасль.</p>
          </Card>
        </div>
      </Section>

      <Section id="roadmap" gray>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-4">
          <Card>
            <h3 className="text-lg font-extrabold">Нед. 1–2</h3>
            <p className="mt-1 text-base text-black">Диагностика, цели, предварительное тестирование, сбор примеров из практики, разработка программы.</p>
          </Card>
          <Card>
            <h3 className="text-lg font-extrabold">Нед. 3–4</h3>
            <p className="mt-1 text-base text-black">Запуск модулей, задания, менторские сессии.</p>
          </Card>
          <Card>
            <h3 className="text-lg font-extrabold">Нед. 5–6</h3>
            <p className="mt-1 text-base text-black">Обучение на рабочем месте, наблюдение, ретроспективное обучение.</p>
          </Card>
          <Card>
            <h3 className="text-lg font-extrabold">Итоги</h3>
            <p className="mt-1 text-base text-black">Посттестирование, отчёт по показателям, план внедрения 30/60/90.</p>
          </Card>
        </div>
      </Section>

      <Section>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <Card>
            <h3 className="text-xl font-extrabold">Кейс</h3>
            <p className="mt-2 text-base text-black">
              <strong>Задача:</strong> прокачать руководителей смен (коммуникации, обратная связь, планирование).
            </p>
            <p className="mt-1 text-base text-black">
              <strong>Достижения:</strong> 6 модулей + групповой коучинг, внедрение индивидуальных и сменных стандартов.
            </p>
            <p className="mt-1 text-base text-black">
              <strong>Результаты через 8 недель:</strong> выполнение 86%, индекс лояльности NPS 63, снижение количества конфликтных ситуаций на 22%.
            </p>
          </Card>
          <Card>
            <h3 className="text-xl font-extrabold">FAQ</h3>
            <details className="group mt-2 border-t border-slate-200 py-3" open>
              <summary className="cursor-pointer text-base font-bold">Можно интеграцию с нашей LMS?</summary>
              <p className="mt-1 text-base text-black">
                Да, выгружаем SCORM/xAPI или ведём обучение в вашей LMS с передачей событий и отчётов.
              </p>
            </details>
            <details className="group border-t border-slate-200 py-3">
              <summary className="cursor-pointer text-base font-bold">Делаете оффлайн?</summary>
              <p className="mt-1 text-base text-black">Да, по РФ. Учтём аренду, логистику и материалы в смете.</p>
            </details>
            <details className="group border-t border-slate-200 py-3">
              <summary className="cursor-pointer text-base font-bold">Вы даёте сертификаты?</summary>
              <p className="mt-1 text-base text-black">
                Да, по итогам прохождения и посттестирование. Для обязательных обучений — работаем с партнёрами, имеющими аккредитацию.
              </p>
            </details>
          </Card>
        </div>
      </Section>

      <Section id="cta" gray>
        <Card className="rounded-2xl shadow-lg p-6">
          <h3 className="text-xl font-extrabold">Запросить программу обучения</h3>
          <form className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <input
              type="text"
              placeholder="Как к вам обращаться (Имя)"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full rounded-xl border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#FF7C22]"
            />
            <input
              type="tel"
              placeholder="+7 (___) ___-__-__ (Телефон)"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full rounded-xl border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#FF7C22]"
            />
            <input
              type="text"
              placeholder="Краткая задача по обучению"
              value={formData.task}
              onChange={(e) => setFormData({ ...formData, task: e.target.value })}
              className="w-full rounded-xl border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#FF7C22]"
            />
            <input
              type="email"
              placeholder="for@example.com"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full rounded-xl border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#FF7C22]"
            />
          </form>

          <div className="mt-4 sm:col-span-2">
            <label className="flex items-start text-xs sm:text-sm cursor-pointer text-slate-600">
              <input
                type="checkbox"
                checked={formData.agreed}
                onChange={(e) => setFormData({ ...formData, agreed: e.target.checked })}
                className="mt-1 mr-2 h-4 w-4 rounded border-slate-300 text-[#003B9B] focus:ring-[#003B9B]"
              />
              <span>
                Я даю{' '}
                <a href="/privacy" className="text-[#003B9B] underline hover:text-[#FF7C22]">
                  согласие на обработку персональных данных
                </a>{' '}
                и соглашаюсь с{' '}
                <a href="/privacy" className="text-[#003B9B] underline hover:text-[#FF7C22]">
                  Политикой конфиденциальности
                </a>
                .
              </span>
            </label>
          </div>

          <button
            onClick={handleSubmit}
            className="mt-4 rounded-xl bg-[#FF7C22] px-6 py-3 text-sm font-bold text-white shadow hover:brightness-[105%] hover:-translate-y-[1px] transition-all duration-200"
          >
            Отправить
          </button>

          {formStatus && <p className={`mt-2 text-xs ${getStatusColorClass(formStatus)}`}>{formStatus}</p>}
        </Card>
      </Section>
    </main>
  );
}